import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useResetConfirm } from './use-reset-confirm';
import { clearChatHistory } from './chat-history.api';

interface ChatReset {
  /** Confirms, then wipes the conversation. Only wired when `reset` is supplied. */
  readonly handleReset: () => void;
  /** Pass to juneau as `confirmReset` when juneau owns the reset call itself. */
  readonly confirmReset: () => Promise<boolean>;
  /** Pass to juneau as `onReset` — drops the server-side history. */
  readonly clearServerHistory: () => void;
  /** Render inside the chat tree so the confirmation dialog mounts. */
  readonly dialog: ReactNode;
}

/**
 * Single definition of "reset the chat": confirm, drop the local conversation,
 * then drop the server-side history.
 * @param reset Local reset from the chat context; omit where juneau calls it itself.
 */
export function useChatReset(reset?: () => void): ChatReset {
  const { confirmReset, dialog } = useResetConfirm();

  const clearServerHistory = useCallback(() => {
    // The local re-save is suppressed because a conversation without a user
    // message is never persisted.
    void clearChatHistory().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error('[ai-chat] Failed to clear chat history', err);
    });
  }, []);

  const handleReset = useCallback(() => {
    // The gate resolves false when the user backs out — nothing is cleared then.
    void confirmReset().then(confirmed => {
      if (!confirmed) return;
      reset?.();
      clearServerHistory();
    });
  }, [clearServerHistory, confirmReset, reset]);

  return { handleReset, confirmReset, clearServerHistory, dialog };
}
